import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, getIdTokenResult } from '../services/authService';
import { db } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        let role = '';
        let accessLevel = '';
        try {
          const tokenResult = await getIdTokenResult(firebaseUser);
          role = tokenResult.claims.role || '';
          accessLevel = tokenResult.claims.accessLevel || '';
        } catch (_) {
          // Claims read failed entirely — fall through to the Firestore
          // fallback below rather than failing closed here.
        }

        // HC-SECURITY-005: custom claims can be missing or stale (set at a
        // prior login, never synced, etc.) — see the investigation at
        // /home/snoopy/investigations/hc-security-005-investigation.md.
        // Firestore is the source of truth; claims are a fast-path cache of
        // it. Only fall back when claims don't already grant platform
        // access — role and accessLevel are always written together by
        // setUserClaims/onUserRoleChanged, so if role is correctly
        // 'platform' via claims, accessLevel from the same token is too.
        if (role !== 'platform') {
          try {
            const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
            const docRole = snap.data()?.role || '';
            const docAccessLevel = snap.data()?.accessLevel || '';
            if (docRole === 'platform') {
              role = docRole;
              accessLevel = docAccessLevel;
            }
          } catch (_) {
            // Firestore fallback failed too — role/accessLevel stay
            // whatever the claims read produced (possibly empty).
          }
        }

        setClaims({ role, accessLevel });
        setUser(firebaseUser);
      } else {
        setUser(null);
        setClaims({});
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  function isPlatformUser() {
    return claims.role === 'platform';
  }

  return (
    <AuthContext.Provider value={{ user, claims, loading, isPlatformUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
